import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBan,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFolderOpen,
  FaSearch,
} from "react-icons/fa";
import ProjectPlan from "./ProjectPlan";
import { Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate, relativeDay } from "./workApi";
import "../dm/dm.css";
import "./work.css";

/** /work/plan/:ptype/:id — plan of any DM or PMS project (also where notification links land). */
export function WorkPlanPage({ api }) {
  const { ptype, id } = useParams();
  const [params] = useSearchParams();
  return (
    <div className="dmp wk">
      <Link to={api.link("work/projects")} className="dmp-back-link">
        <FaArrowLeft aria-hidden="true" /> All projects
      </Link>
      <ProjectPlan api={api} projectType={ptype} projectId={Number(id)} initialItemId={Number(params.get("item")) || null} />
    </div>
  );
}

const TYPES = [
  { key: "", label: "All types" },
  { key: "dm", label: "Marketing" },
  { key: "pms", label: "Delivery" },
];

const VIEWS = [
  { key: "all", label: "All", test: () => true },
  { key: "attention", label: "Needs attention", test: (p) => p.Items_Late > 0 || p.Items_Blocked > 0 },
  { key: "unplanned", label: "Not planned yet", test: (p) => !p.Lanes },
  { key: "active", label: "In progress", test: (p) => p.Lanes > 0 && p.Items_Done < p.Items_Total },
];

/** Late / stuck work first, then projects still to plan, then the rest by name. */
const rank = (p) => (p.Items_Late ? 0 : p.Items_Blocked ? 1 : !p.Lanes ? 2 : 3);

function Progress({ p }) {
  if (!p.Lanes) {
    return <span className="wk-pill wk-pill--grey wk-pill--sm">Not planned</span>;
  }
  if (!p.Items_Total) {
    return <span className="wk-muted">{p.Lanes} area(s), no deliverables yet</span>;
  }
  const pct = Math.round((p.Items_Done / p.Items_Total) * 100);
  return (
    <div className="wk-proj-progress">
      <div className="wk-progress" aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </div>
      <span className="wk-proj-progress__text">
        {p.Items_Done} of {p.Items_Total} done
      </span>
    </div>
  );
}

function Attention({ p }) {
  if (!p.Lanes) return <span className="wk-muted">—</span>;
  if (!p.Items_Late && !p.Items_Blocked) {
    return (
      <span className="wk-pill wk-pill--green wk-pill--sm">
        <FaCheckCircle aria-hidden="true" className="wk-pill__icon" />
        On track
      </span>
    );
  }
  return (
    <span className="wk-proj-flags">
      {p.Items_Late ? (
        <span className="wk-pill wk-pill--red wk-pill--sm">
          <FaExclamationTriangle aria-hidden="true" className="wk-pill__icon" />
          {p.Items_Late} late
        </span>
      ) : null}
      {p.Items_Blocked ? (
        <span className="wk-pill wk-pill--amber wk-pill--sm">
          <FaBan aria-hidden="true" className="wk-pill__icon" />
          {p.Items_Blocked} stuck
        </span>
      ) : null}
    </span>
  );
}

/** Pick a project to plan and assign work: what needs a decision shows first. */
export function WorkProjects({ api }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [view, setView] = useState("all");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .projects({ search: search || undefined, limit: 100 })
        .then((r) => {
          setRows(r || []);
          setError("");
        })
        .catch((err) => setError(errorMessage(err, "Could not load projects")));
    }, 250);
    return () => clearTimeout(t);
  }, [api, search]);

  const ofType = useMemo(() => (rows || []).filter((p) => !type || p.Project_Type === type), [rows, type]);
  const shown = useMemo(() => {
    const test = (VIEWS.find((v) => v.key === view) || VIEWS[0]).test;
    return ofType
      .filter(test)
      .sort((a, b) => rank(a) - rank(b) || String(a.Project_Name).localeCompare(String(b.Project_Name)));
  }, [ofType, view]);
  const open = (p) => navigate(api.link(`work/plan/${p.Project_Type}/${p.Project_Id}`));

  return (
    <div className="dmp wk">
      <PageHead
        title="Plan & Assign"
        info="Open a project to split its work into areas (Video, Design, Campaigns…), give each piece to a person with a due date, and pick who reviews it."
      />
      <div className="dmp-card">
        <div className="wk-proj-toolbar">
          <div className="wk-search">
            <FaSearch aria-hidden="true" className="wk-search__icon" />
            <label className="wk-sr-only" htmlFor="wk-proj-search">
              Search projects
            </label>
            <input
              id="wk-proj-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects"
              autoComplete="off"
            />
          </div>
          <div className="wk-segmented" role="group" aria-label="Project type">
            {TYPES.map((t) => (
              <button key={t.key || "all"} type="button" aria-pressed={type === t.key} onClick={() => setType(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="wk-chip-row" role="group" aria-label="Show">
          {VIEWS.map((v) => {
            const count = ofType.filter(v.test).length;
            return (
              <button
                key={v.key}
                type="button"
                className={`wk-chip${v.key === "attention" && count ? " wk-chip--alert" : ""}`}
                aria-pressed={view === v.key}
                onClick={() => setView(v.key)}
              >
                {v.label} <span className="wk-chip__count">{rows ? count : "…"}</span>
              </button>
            );
          })}
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}
        {rows === null ? (
          <div className="wk-muted">Loading…</div>
        ) : shown.length ? (
          <table className="wk-table wk-proj-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Owner</th>
                <th>Progress</th>
                <th>Needs attention</th>
                <th>Next date</th>
                <th>
                  <span className="wk-sr-only">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={`${p.Project_Type}-${p.Project_Id}`} className="wk-proj-row" onClick={() => open(p)}>
                  <td data-label="Project">
                    <Link
                      to={api.link(`work/plan/${p.Project_Type}/${p.Project_Id}`)}
                      className="wk-proj-name"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {p.Project_Name}
                    </Link>
                    <div className="wk-proj-meta">
                      <span className={`wk-proj-type wk-proj-type--${p.Project_Type}`}>
                        {p.Project_Type === "dm" ? "Marketing" : "Delivery"}
                      </span>
                      {api.isStaff && p.Tenant_Name ? <span>{p.Tenant_Name}</span> : null}
                    </div>
                  </td>
                  <td data-label="Owner">
                    {p.Owner_Name ? (
                      <span className="wk-card__who">
                        <Avatar name={p.Owner_Name} size={24} />
                        <span>{p.Owner_Name}</span>
                      </span>
                    ) : (
                      <span className="wk-muted">No owner yet</span>
                    )}
                  </td>
                  <td data-label="Progress">
                    <Progress p={p} />
                  </td>
                  <td data-label="Needs attention">
                    <Attention p={p} />
                  </td>
                  <td data-label="Next date">
                    {p.Next_Date ? (
                      <>
                        {fmtShortDate(p.Next_Date)}
                        <div className="wk-muted" style={{ fontSize: 12 }}>
                          {relativeDay(p.Next_Date)}
                        </div>
                      </>
                    ) : (
                      <span className="wk-muted">—</span>
                    )}
                  </td>
                  <td className="wk-proj-action">
                    <button
                      type="button"
                      className={`dmp-btn dmp-btn--sm${p.Lanes ? " dmp-btn--ghost" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        open(p);
                      }}
                      aria-label={`${p.Lanes ? "Open plan of" : "Start planning"} ${p.Project_Name}`}
                    >
                      {p.Lanes ? "Open plan" : "Start planning"} <FaArrowRight aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty icon={FaFolderOpen} title={rows.length ? "Nothing here" : "No projects yet"}>
            {rows.length
              ? "No project matches this filter."
              : "Marketing projects (Digital Marketing) and delivery projects show up here to plan."}
          </Empty>
        )}
      </div>
    </div>
  );
}

export default WorkPlanPage;
